import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import * as actions from "../../../../_redux/attendance/AttendanceAction";
import { AbsenteeismStatusOptions } from "../../../../../../../_helpers/Helpers";
import * as uiHelpers from "../StudentsUIHelpers";
import {
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../../_metronic/_partials/controls";
import { useStudentsUIContext } from "../StudentsUIContext";

export function StudentsTable() {
  // Students UI Context
  const studentsUIContext = useStudentsUIContext();
  const studentsUIProps = useMemo(() => {
    return {
      sectionId: studentsUIContext.sectionId,
      queryParams: studentsUIContext.queryParams,
      setQueryParams: studentsUIContext.setQueryParams,
      absenteeismValues: studentsUIContext.absenteeismValues,
      setAbsenteeismValues: studentsUIContext.setAbsenteeismValues
    };
  }, [studentsUIContext]);

  // Getting curret state of Students list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.attendance }),
    shallowEqual
  );
  const { totalCount, dailyCourseHour, entities, listLoading } = currentState;
  
  // Students Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    if (studentsUIProps.sectionId) dispatch(actions.getStudentAbsenteeisms(studentsUIProps.queryParams));
    return () => dispatch(actions.cleanAttendanceState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "studentNumber",
      text: "Öğrenci Numarası",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "180px"
      }
    },
    {
      dataField: "studentName",
      text: "Adı Soyadı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    }
  ];

  for (let i = 1; i <= dailyCourseHour; i++) {
    const column = {
      dataField: "courseHour" + i,
      text: i + ". Ders",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "120px"
      }
    };
    columns.push(column);
  }

  columns.push(
    {
      dataField: "absenteeismStatus",
      text: "Durum",
      formatter: columnFormatters.AbsenteeismColumnFormatter,
      formatExtraData: {
        absenteeismValues: studentsUIProps.absenteeismValues,
        setAbsenteeismValues: studentsUIProps.setAbsenteeismValues,
        options: AbsenteeismStatusOptions
      },
      headerClasses: "pl-22 pr-22"
    },
    {
      dataField: "explanation",
      text: "Açıklama",
      formatter: columnFormatters.ExplanationColumnFormatter,
      formatExtraData: {
        absenteeismValues: studentsUIProps.absenteeismValues,
        setAbsenteeismValues: studentsUIProps.setAbsenteeismValues,
      },
      headerClasses: "pl-40 pr-40"
    },
    {
      dataField: "application",
      text: "Uygulama",
      formatter: columnFormatters.ApplicationColumnFormatter,
      formatExtraData: {
        absenteeismValues: studentsUIProps.absenteeismValues,
        setAbsenteeismValues: studentsUIProps.setAbsenteeismValues,
      },
      classes: "text-center"
    },
    {
      dataField: "mail",
      text: "Mail",
      formatter: columnFormatters.MailColumnFormatter,
      formatExtraData: {
        absenteeismValues: studentsUIProps.absenteeismValues,
        setAbsenteeismValues: studentsUIProps.setAbsenteeismValues,
      },
      classes: "text-center"
    },
    {
      dataField: "sms",
      text: "SMS",
      formatter: columnFormatters.SmsColumnFormatter,
      formatExtraData: {
        absenteeismValues: studentsUIProps.absenteeismValues,
        setAbsenteeismValues: studentsUIProps.setAbsenteeismValues,
      },
      classes: "text-center"
    }
  );

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: studentsUIProps.queryParams.pageSize,
    page: studentsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                studentsUIProps.setQueryParams
              )}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}